import { z } from "zod";
import type { RestorationPost } from "@/lib/server-actions/restoration/_shared";

const strings = z.array(z.string());
const block = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), content: z.string() }),
  z.object({ type: z.literal("code"), content: z.string(), language: z.string().optional() }),
  z.object({ type: z.literal("table"), headers: strings, rows: z.array(strings) }),
  z.object({
    type: z.literal("image"), url: z.string(), alt: z.string().optional(),
    width: z.number().optional(), height: z.number().optional(),
  }),
]);

// 게시용 검증과 구분해 빈칸과 미완성 선택지도 그대로 보존한다.
const draftSchema = z.object({
  blocks: z.array(block), choices: strings, selectedChoices: strings,
  answerMode: z.enum(["short", "choice"]), shortAnswer: z.string(),
  preamble: z.string(), closing: z.string(),
  problemNumber: z.number().int().min(1).max(20).nullable(),
  adminStatus: z.enum(["confirmed", "editing"]),
});
export type RestorationDraft = z.infer<typeof draftSchema>;

export function initialRestorationDraft(post?: RestorationPost): RestorationDraft {
  const answer = post?.body.correctAnswer;
  return {
    blocks: post?.body.blocks ?? [{ type: "text", content: "" }],
    choices: post?.body.choices ?? [],
    selectedChoices: Array.isArray(answer) ? answer :
      typeof answer === "string" && post?.body.choices?.includes(answer) ? [answer] : [],
    answerMode: post?.body.choices?.length ? "choice" : "short",
    shortAnswer: typeof answer === "string" ? answer : "",
    preamble: post?.preamble_text ?? "", closing: post?.closing_text ?? "",
    problemNumber: post?.problem_number ?? null,
    adminStatus: post?.post_status ?? "editing",
  };
}

export function restorationDraftKey(userId: string, sessionId: number, postId?: number) {
  return `restoration-draft:v1:${encodeURIComponent(userId)}:${sessionId}:${postId === undefined ? "new" : `edit-${postId}`}`;
}

// 저장 실패 시에도 닫기·레이아웃 전환 동안 현재 페이지 메모리에 입력을 보관한다.
// null은 삭제 실패 시 오래된 localStorage 내용을 다시 복구하지 않게 하는 표시다.
const memoryDrafts = new Map<string, RestorationDraft | null>();
const failedKeys = new Set<string>();
const storageListeners = new Set<() => void>();
export function subscribeRestorationStorage(listener: () => void) {
  storageListeners.add(listener);
  return () => { storageListeners.delete(listener); };
}
export function restorationStorageFailed(key: string) {
  return failedKeys.has(key);
}
function reportStorageResult(key: string, failed: boolean) {
  const changed = failedKeys.has(key) !== failed;
  if (failed) failedKeys.add(key);
  else failedKeys.delete(key);
  if (changed) storageListeners.forEach((listener) => listener());
}

export function readRestorationDraft(key: string): RestorationDraft | null {
  if (memoryDrafts.has(key)) return memoryDrafts.get(key) ?? null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = draftSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writeRestorationDraft(key: string, draft: RestorationDraft | null): boolean {
  memoryDrafts.set(key, draft);
  try {
    if (draft) localStorage.setItem(key, JSON.stringify(draft));
    else localStorage.removeItem(key);
    reportStorageResult(key, false);
    return true;
  } catch {
    reportStorageResult(key, true);
    return false;
  }
}

// 외부 닫기 버튼(데스크톱 셸·모바일 토글)도 에디터의 유실 확인을 거친다.
const leaveGuards = new Set<() => boolean>();
export function registerRestorationLeaveGuard(guard: () => boolean) {
  leaveGuards.add(guard);
  return () => { leaveGuards.delete(guard); };
}
export function confirmRestorationLeave() {
  return [...leaveGuards].every((guard) => guard());
}
